import { motion } from "framer-motion";
import { ArrowRight, Star, Coffee, Sparkles } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { BRAND } from "../lib/menuData";

export const Hero = () => {
  const { t } = useLang();
  const waLink = `https://wa.me/${BRAND.phone.replace("+", "")}?text=${encodeURIComponent(
    "Halo Hayalan Coffee, saya mau booking VIP Room."
  )}`;

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-16 md:pt-32 md:pb-24 overflow-hidden bg-brand-cream"
    >
      {/* Decorative blue glow */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand-blue/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[360px] h-[360px] rounded-full bg-brand-gold/20 blur-[100px] pointer-events-none" />

      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-border bg-white/70 backdrop-blur-sm mb-8"
            >
              <Sparkles
                className="w-3.5 h-3.5 text-brand-blue"
                strokeWidth={2}
              />
              <span className="text-xs font-medium uppercase tracking-[0.22em] text-brand-ink">
                {t.hero.eyebrow}
              </span>
            </motion.div>

            <h1 className="font-serif text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-7xl xl:text-[5.4rem] font-medium tracking-tight text-brand-ink">
              <motion.span
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="block"
              >
                {t.hero.title_1}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.22 }}
                className="block text-brand-blue italic"
              >
                {t.hero.title_2}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.34 }}
                className="block"
              >
                {t.hero.title_3}
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.46 }}
              className="mt-7 text-base md:text-lg text-brand-mocha max-w-xl leading-relaxed"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.58 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="/menu"
                data-testid="hero-menu-cta"
                className="group inline-flex items-center gap-2 bg-brand-blue text-white px-7 py-4 rounded-full font-medium transition-all hover:bg-blue-800 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-brand-blue/25"
              >
                {t.hero.cta_menu}
                <ArrowRight
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-booking-cta"
                className="inline-flex items-center gap-2 border border-brand-coffee text-brand-coffee px-7 py-4 rounded-full font-medium transition-all hover:bg-brand-coffee hover:text-brand-cream"
              >
                {t.hero.cta_booking}
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-14 grid grid-cols-3 gap-4 max-w-lg"
            >
              <div className="border-l-2 border-brand-blue pl-4">
                <div className="flex items-center gap-1 text-brand-ink">
                  <span className="font-serif text-3xl md:text-4xl">4.7</span>
                  <Star
                    className="w-4 h-4 fill-brand-gold text-brand-gold"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="mt-1 text-xs uppercase tracking-widest text-brand-mocha">
                  {t.hero.stat_rating}
                </p>
              </div>
              <div className="border-l-2 border-brand-coffee/30 pl-4">
                <div className="flex items-center gap-1 text-brand-ink">
                  <span className="font-serif text-3xl md:text-4xl">40+</span>
                </div>
                <p className="mt-1 text-xs uppercase tracking-widest text-brand-mocha">
                  {t.hero.stat_menu}
                </p>
              </div>
              <div className="border-l-2 border-brand-coffee/30 pl-4">
                <div className="flex items-center gap-1 text-brand-ink">
                  <span className="font-serif text-3xl md:text-4xl">22:00</span>
                </div>
                <p className="mt-1 text-xs uppercase tracking-widest text-brand-mocha">
                  {t.hero.stat_hours}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right: hero image stack */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden ring-1 ring-brand-border shadow-2xl shadow-brand-coffee/10">
              <img
                src={BRAND.hero}
                alt="Premium latte at Hayalan Coffee"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-coffee/40 via-transparent to-transparent" />
            </div>

            {/* Floating storefront card */}
            <motion.div
              initial={{ opacity: 0, x: -20, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="hidden md:block absolute -bottom-8 -left-10 w-56 rounded-2xl overflow-hidden ring-1 ring-brand-border shadow-xl"
            >
              <img
                src={BRAND.storefront}
                alt="Hayalan Coffee storefront"
                className="w-full h-36 object-cover"
              />
              <div className="bg-white px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-brand-blue font-medium">
                  Storefront
                </p>
                <p className="text-sm font-medium text-brand-ink mt-0.5">
                  Pangkalpinang, Babel
                </p>
              </div>
            </motion.div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="hidden md:flex absolute -top-4 -right-4 w-28 h-28 rounded-full bg-brand-blue text-white items-center justify-center flex-col shadow-xl shadow-brand-blue/30"
            >
              <Coffee className="w-5 h-5" strokeWidth={1.8} />
              <span className="font-serif text-xs italic mt-1 leading-tight text-center">
                Sejak
                <br />
                hayalan
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom marquee */}
      <div className="mt-20 md:mt-24 border-y border-brand-border/60 overflow-hidden py-5 bg-brand-sand/40">
        <div className="flex animate-marquee whitespace-nowrap gap-12 text-2xl md:text-3xl font-serif italic text-brand-coffee/80">
          {Array(2)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="flex gap-12 items-center">
                <span>Hayalan Series</span>
                <span className="text-brand-blue">✦</span>
                <span>Arabica Coffee</span>
                <span className="text-brand-gold">✦</span>
                <span>VIP Meeting Room</span>
                <span className="text-brand-blue">✦</span>
                <span>Aesthetic Spots</span>
                <span className="text-brand-gold">✦</span>
                <span>Open till 22:00</span>
                <span className="text-brand-blue">✦</span>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
};
